import React, { useCallback } from 'react';
import { Loader2, Box } from 'lucide-react';
import type { ScreenComponentDef } from '@core/types/slot-screen-component.types';

interface ComponentCardProps {
  component: ScreenComponentDef;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDoubleClick: (id: string) => void;
}

function getSelectedImageUrl(component: ScreenComponentDef): string | null {
  if (!component.selectedResultId) return null;
  const result = component.generatedResults.find(
    r => r.id === component.selectedResultId && r.status === 'ready' && r.imageUrl
  );
  return result?.imageUrl ?? null;
}

export function ComponentCard({
  component,
  isSelected,
  onSelect,
  onDoubleClick,
}: ComponentCardProps) {
  const imageUrl = getSelectedImageUrl(component);
  const isGenerating = component.status === 'generating';
  const hasResult = component.selectedResultId !== null;

  const handleClick = useCallback(() => {
    onSelect(component.id);
  }, [component.id, onSelect]);

  const handleDoubleClick = useCallback(() => {
    onDoubleClick(component.id);
  }, [component.id, onDoubleClick]);

  return (
    <div
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      className={`flex-shrink-0 w-[80px] cursor-pointer select-none rounded transition-all ${
        isSelected
          ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-zinc-900'
          : 'hover:bg-zinc-800'
      }`}
    >
      {/* Thumbnail area */}
      <div
        className="w-full h-[52px] rounded-t overflow-hidden bg-zinc-800/60 flex items-center justify-center"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={component.name}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-[44px] h-[36px] border border-dashed border-zinc-600 rounded-sm flex items-center justify-center">
            <Box size={14} className="text-zinc-600" />
          </div>
        )}
      </div>

      {/* Label and status */}
      <div className="flex items-center gap-1 px-1 py-1">
        {/* Status dot */}
        {isGenerating ? (
          <Loader2 size={8} className="text-blue-400 animate-spin flex-shrink-0" />
        ) : hasResult ? (
          <span className="inline-block w-[6px] h-[6px] rounded-full bg-green-500 flex-shrink-0" />
        ) : (
          <span className="inline-block w-[6px] h-[6px] rounded-full border border-zinc-600 flex-shrink-0" />
        )}

        <span className="text-[9px] text-zinc-400 truncate leading-tight">
          {component.name}
        </span>
      </div>
    </div>
  );
}
